import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { maintenanceApi, Maintenance } from '../services/maintenanceApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Maintenances: React.FC = () => {
    const { t } = useLanguage();
    
    const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingMaintenance, setEditingMaintenance] = useState<Maintenance | null>(null);
    const [formData, setFormData] = useState<Partial<Maintenance>>({
        title: '',
        type: 'preventive',
        category: 'engine',
        status: 'scheduled',
        priority: 'medium',
        vehicleId: 0
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [mRes, vRes] = await Promise.all([
                maintenanceApi.getAll(),
                vehiclesApi.getAll()
            ]);

            if (mRes.success) setMaintenances(mRes.data);
            if (vRes.success) setVehicles(vRes.data);

            if (!mRes.success) blobticMessage.error(t('common.messages.error') || 'Error', mRes.message || 'Unknown error');
        } catch (error) {
            console.error('Failed to load maintenance data:', error);
            blobticMessage.error(t('common.messages.error') || 'Error', 'Failed to load maintenance data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleOpenDialog = (maintenance?: Maintenance) => {
        if (maintenance) {
            setEditingMaintenance(maintenance);
            setFormData({ ...maintenance });
        } else {
            setEditingMaintenance(null);
            setFormData({
                title: '',
                type: 'preventive',
                category: 'engine',
                status: 'scheduled',
                priority: 'medium',
                vehicleId: vehicles.length > 0 ? vehicles[0].id : 0
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            let response;
            if (editingMaintenance) {
                response = await maintenanceApi.update(editingMaintenance.id, formData);
            } else {
                response = await maintenanceApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setIsDialogOpen(false);
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving maintenance:', error);
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm'))) return;
        try {
            const response = await maintenanceApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted'));
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const columns = [
        { id: 'title', label: t('parc.maintenance.mainTitle'), minWidth: 150 },
        { id: 'vehicleRegistration', label: t('parc.maintenance.vehicle'), minWidth: 120 },
        {
            id: 'type',
            label: t('parc.maintenance.type'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'preventive') return t('parc.maintenance.typePreventive');
                if (value === 'corrective') return t('parc.maintenance.typeCorrective');
                if (value === 'emergency') return t('parc.maintenance.typeEmergency');
                return value;
            }
        },
        {
            id: 'category',
            label: t('parc.maintenance.category'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'engine') return t('parc.maintenance.catEngine');
                if (value === 'transmission') return t('parc.maintenance.catTransmission');
                if (value === 'brakes') return t('parc.maintenance.catBrakes');
                if (value === 'electrical') return t('parc.maintenance.catElectrical');
                if (value === 'body') return t('parc.maintenance.catBody');
                if (value === 'tires') return t('parc.maintenance.catTires');
                if (value === 'other') return t('parc.maintenance.catOther');
                return value;
            }
        },
        {
            id: 'status',
            label: t('parc.maintenance.status'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'scheduled') return t('parc.maintenance.statusScheduled');
                if (value === 'in_progress') return t('parc.maintenance.statusInProgress');
                if (value === 'completed') return t('parc.maintenance.statusCompleted');
                if (value === 'cancelled') return t('parc.maintenance.statusCancelled');
                return value;
            }
        },
        {
            id: 'priority',
            label: t('parc.maintenance.priority'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'low') return t('parc.maintenance.priorityLow');
                if (value === 'medium') return t('parc.maintenance.priorityMedium');
                if (value === 'high') return t('parc.maintenance.priorityHigh');
                if (value === 'urgent') return t('parc.maintenance.priorityUrgent');
                return value;
            }
        }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.maintenance.newMaintenance')}
        </Button>
    );

    return (
        <GenericPage title={t('parc.maintenance.title')} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={maintenances}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingMaintenance ? t('parc.maintenance.editMaintenance') : t('parc.maintenance.newMaintenance')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('parc.maintenance.mainTitle')}
                                value={formData.title || ''}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.maintenance.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehicleId || ''}
                                    label={t('parc.maintenance.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehicleId: Number(e.target.value) })}
                                >
                                    {vehicles.map(v => (
                                        <MenuItem key={v.id} value={v.id}>{v.registrationNumber}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.maintenance.type')}</InputLabel>
                                <Select
                                    value={formData.type || 'preventive'}
                                    label={t('parc.maintenance.type')}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                >
                                    <MenuItem value="preventive">{t('parc.maintenance.typePreventive')}</MenuItem>
                                    <MenuItem value="corrective">{t('parc.maintenance.typeCorrective')}</MenuItem>
                                    <MenuItem value="emergency">{t('parc.maintenance.typeEmergency')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.maintenance.category')}</InputLabel>
                                <Select
                                    value={formData.category || 'engine'}
                                    label={t('parc.maintenance.category')}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    <MenuItem value="engine">{t('parc.maintenance.catEngine')}</MenuItem>
                                    <MenuItem value="transmission">{t('parc.maintenance.catTransmission')}</MenuItem>
                                    <MenuItem value="brakes">{t('parc.maintenance.catBrakes')}</MenuItem>
                                    <MenuItem value="electrical">{t('parc.maintenance.catElectrical')}</MenuItem>
                                    <MenuItem value="body">{t('parc.maintenance.catBody')}</MenuItem>
                                    <MenuItem value="tires">{t('parc.maintenance.catTires')}</MenuItem>
                                    <MenuItem value="other">{t('parc.maintenance.catOther')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.maintenance.priority')}</InputLabel>
                                <Select
                                    value={formData.priority || 'medium'}
                                    label={t('parc.maintenance.priority')}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                >
                                    <MenuItem value="low">{t('parc.maintenance.priorityLow')}</MenuItem>
                                    <MenuItem value="medium">{t('parc.maintenance.priorityMedium')}</MenuItem>
                                    <MenuItem value="high">{t('parc.maintenance.priorityHigh')}</MenuItem>
                                    <MenuItem value="urgent">{t('parc.maintenance.priorityUrgent')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.maintenance.status')}</InputLabel>
                                <Select
                                    value={formData.status || 'scheduled'}
                                    label={t('parc.maintenance.status')}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <MenuItem value="scheduled">{t('parc.maintenance.statusScheduled')}</MenuItem>
                                    <MenuItem value="in_progress">{t('parc.maintenance.statusInProgress')}</MenuItem>
                                    <MenuItem value="completed">{t('parc.maintenance.statusCompleted')}</MenuItem>
                                    <MenuItem value="cancelled">{t('parc.maintenance.statusCancelled')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.maintenance.cost')}
                                value={formData.cost || ''}
                                onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Maintenances;
